/**
 * api.js — Global Axios configuration for the NestJS backend.
 *
 * Imported once in index.js so the interceptors apply to EVERY axios call
 * throughout the app without changing any import statements.
 *
 * What it does:
 *  1. Request interceptor  — injects the Bearer token from localStorage on
 *     every outgoing request (belt-and-suspenders alongside AuthContext).
 *  2. Response interceptor — normalises NestJS error shape to the FastAPI
 *     shape that all existing components rely on:
 *       NestJS  → { statusCode, message: string | string[], error }
 *       FastAPI → { detail: string }
 *     After normalisation both `error.response.data.detail` AND
 *     `error.response.data.message` will work.
 */

import axios from 'axios';

// ── Request interceptor ──────────────────────────────────────────────────────
axios.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token && !config.headers['Authorization']) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error),
);

// ── Response error interceptor ───────────────────────────────────────────────
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.data) {
      const data = error.response.data;

      // NestJS ValidationPipe returns message as string[].
      // Flatten it to a single string so toast.error() looks nice.
      if (Array.isArray(data.message)) {
        data.message = data.message.join('; ');
      }

      // Copy NestJS `message` → `detail` so existing code like
      // `error.response?.data?.detail || 'fallback'` keeps working.
      if (!data.detail && data.message) {
        data.detail = data.message;
      }
    }
    return Promise.reject(error);
  },
);

export default axios;
